import { apiClient } from './api'
import type {
  CustomDomainConfig,
  DomainVerificationResult,
  DnsInstructions,
  DomainVerificationLog,
  AddCustomDomainRequest,
  ClaimSubdomainRequest,
  UpdateWhiteLabelRequest,
  DomainAvailabilityResult
} from '@/types/shop'

/**
 * Service for managing shop custom domains and Zaveato subdomains.
 * Allows shop owners to:
 * - Connect their own domain (e.g., www.mybusiness.com)
 * - Claim a Zaveato subdomain (e.g., mybusiness.zaveato.co.uk)
 * - Configure white-label options
 * - Manage SSL certificates
 */
export class CustomDomainService {
  private static readonly BASE_URL = '/shop/custom-domain'

  /**
   * Get the current custom domain configuration for a shop.
   */
  static async getConfiguration(shopId: string): Promise<CustomDomainConfig> {
    return apiClient.get<CustomDomainConfig>(`${this.BASE_URL}/${shopId}`)
  }

  /**
   * Add a custom domain to a shop.
   * The domain will need DNS verification before it becomes active.
   */
  static async addCustomDomain(
    shopId: string,
    request: AddCustomDomainRequest
  ): Promise<CustomDomainConfig> {
    return apiClient.post<CustomDomainConfig>(`${this.BASE_URL}/${shopId}`, request)
  }

  /**
   * Remove the custom domain from a shop.
   */
  static async removeCustomDomain(shopId: string): Promise<void> {
    return apiClient.delete<void>(`${this.BASE_URL}/${shopId}`)
  }

  /**
   * Verify the DNS configuration for a custom domain.
   * This checks if the domain's DNS records point to Zaveato.
   */
  static async verifyDomain(shopId: string): Promise<DomainVerificationResult> {
    return apiClient.post<DomainVerificationResult>(`${this.BASE_URL}/${shopId}/verify`)
  }

  /**
   * Get DNS setup instructions for a custom domain.
   */
  static async getDnsInstructions(shopId: string): Promise<DnsInstructions> {
    return apiClient.get<DnsInstructions>(`${this.BASE_URL}/${shopId}/dns-instructions`)
  }

  /**
   * Manually trigger SSL certificate provisioning.
   * This is typically called after DNS verification succeeds.
   */
  static async provisionSsl(shopId: string): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(`${this.BASE_URL}/${shopId}/provision-ssl`)
  }

  /**
   * Check and update SSL/ACM certificate status.
   * Actively queries ACM for certificate status and updates domain if certificate is issued.
   * This provides self-correcting behavior - if certificate is issued but status is stale,
   * it will attach the certificate and activate the domain.
   */
  static async checkSslStatus(shopId: string): Promise<CustomDomainConfig> {
    return apiClient.post<CustomDomainConfig>(`${this.BASE_URL}/${shopId}/check-ssl-status`)
  }

  /**
   * Register the shop's verified custom domain with Stripe so the Apple Pay
   * button renders in the Payment Element. Triggered manually from settings
   * once the shop owner has confirmed their WL site looks right.
   */
  static async registerApplePay(shopId: string): Promise<CustomDomainConfig> {
    return apiClient.post<CustomDomainConfig>(`${this.BASE_URL}/${shopId}/apple-pay/register`)
  }

  /**
   * Re-check the Apple Pay registration status with Stripe (for the
   * "Check Status" action when registration is Pending).
   */
  static async checkApplePayStatus(shopId: string): Promise<CustomDomainConfig> {
    return apiClient.post<CustomDomainConfig>(`${this.BASE_URL}/${shopId}/apple-pay/check`)
  }

  /**
   * Claim a Zaveato subdomain (e.g., mybusiness.zaveato.co.uk).
   * Subdomains are instantly active without DNS configuration.
   */
  static async claimSubdomain(
    shopId: string,
    request: ClaimSubdomainRequest
  ): Promise<CustomDomainConfig> {
    return apiClient.post<CustomDomainConfig>(`${this.BASE_URL}/${shopId}/subdomain`, request)
  }

  /**
   * Release a Zaveato subdomain.
   */
  static async releaseSubdomain(shopId: string): Promise<void> {
    return apiClient.delete<void>(`${this.BASE_URL}/${shopId}/subdomain`)
  }

  /**
   * Update white-label settings (hide branding, redirect).
   */
  static async updateWhiteLabelSettings(
    shopId: string,
    request: UpdateWhiteLabelRequest
  ): Promise<CustomDomainConfig> {
    return apiClient.put<CustomDomainConfig>(`${this.BASE_URL}/${shopId}/white-label`, request)
  }

  /**
   * Get the shop's current white-label branding fields (theme, favicon, font, tagline).
   */
  static async getBranding(shopId: string): Promise<ShopBrandingPayload> {
    return apiClient.get<ShopBrandingPayload>(`${this.BASE_URL}/${shopId}/branding`)
  }

  /**
   * Save the shop's white-label branding fields. Returns 204 No Content.
   */
  static async updateBranding(shopId: string, request: UpdateShopBrandingRequest): Promise<void> {
    return apiClient.put<void>(`${this.BASE_URL}/${shopId}/branding`, request)
  }

  /**
   * Get domain verification history.
   */
  static async getVerificationHistory(
    shopId: string,
    limit = 10
  ): Promise<DomainVerificationLog[]> {
    return apiClient.get<DomainVerificationLog[]>(
      `${this.BASE_URL}/${shopId}/verification-history`,
      { params: { limit } }
    )
  }

  /**
   * Check if a domain is available.
   */
  static async checkDomainAvailability(domain: string): Promise<DomainAvailabilityResult> {
    return apiClient.get<DomainAvailabilityResult>(`${this.BASE_URL}/check-availability`, {
      params: { domain }
    })
  }

  /**
   * Check if a subdomain is available.
   */
  static async checkSubdomainAvailability(subdomain: string): Promise<DomainAvailabilityResult> {
    return apiClient.get<DomainAvailabilityResult>(`${this.BASE_URL}/check-availability`, {
      params: { subdomain }
    })
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Get the full URL for a Zaveato subdomain.
   */
  static getSubdomainUrl(subdomain: string): string {
    const baseDomain = import.meta.env.VITE_BASE_DOMAIN || 'zaveato.co.uk'
    return `https://${subdomain}.${baseDomain}`
  }

  /**
   * Get the full URL for a custom domain.
   */
  static getCustomDomainUrl(domain: string): string {
    return `https://${domain}`
  }

  /**
   * Check if a subdomain is valid (alphanumeric, hyphens, 3-63 chars).
   */
  static isValidSubdomain(subdomain: string): { valid: boolean; error?: string } {
    if (!subdomain) {
      return { valid: false, error: 'Subdomain is required' }
    }

    const trimmed = subdomain.trim().toLowerCase()

    if (trimmed.length < 3) {
      return { valid: false, error: 'Subdomain must be at least 3 characters' }
    }

    if (trimmed.length > 63) {
      return { valid: false, error: 'Subdomain must be at most 63 characters' }
    }

    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(trimmed)) {
      return {
        valid: false,
        error: 'Subdomain can only contain letters, numbers, and hyphens (not at start/end)'
      }
    }

    // Reserved subdomains
    const reserved = ['www', 'api', 'staging', 'admin', 'app', 'mail', 'ftp', 'dev', 'test']
    if (reserved.includes(trimmed)) {
      return { valid: false, error: 'This subdomain is reserved' }
    }

    return { valid: true }
  }

  /**
   * Check if a domain is valid format.
   */
  static isValidDomain(domain: string): { valid: boolean; error?: string } {
    if (!domain) {
      return { valid: false, error: 'Domain is required' }
    }

    const trimmed = domain.trim().toLowerCase()

    // Basic domain regex
    const domainRegex = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/
    if (!domainRegex.test(trimmed)) {
      return { valid: false, error: 'Invalid domain format' }
    }

    // Blocked domains (Zaveato domains should use subdomain feature)
    const baseDomain = import.meta.env.VITE_BASE_DOMAIN || 'zaveato.co.uk'
    if (trimmed.endsWith(`.${baseDomain}`) || trimmed.endsWith('.zaveato.co.uk') || trimmed.endsWith('.zaveato.com')) {
      return {
        valid: false,
        error: 'For Zaveato domains, use the subdomain feature instead'
      }
    }

    return { valid: true }
  }

  /**
   * Get human-readable status text.
   */
  static getStatusText(status: string): string {
    const statusMap: Record<string, string> = {
      None: 'Not configured',
      Pending: 'Pending verification',
      DnsVerified: 'DNS verified',
      Active: 'Active',
      Failed: 'Verification failed',
      Suspended: 'Suspended',
      Provisioning: 'Provisioning SSL',
      RenewalPending: 'SSL renewal pending',
      Expired: 'SSL expired'
    }
    return statusMap[status] || status
  }

  /**
   * Get status color class for styling.
   */
  static getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      None: 'text-gray-500',
      Pending: 'text-amber-600',
      DnsVerified: 'text-blue-600',
      Active: 'text-green-600',
      Failed: 'text-red-600',
      Suspended: 'text-red-600',
      Provisioning: 'text-amber-600',
      RenewalPending: 'text-amber-600',
      Expired: 'text-red-600'
    }
    return colorMap[status] || 'text-gray-500'
  }
}

export default CustomDomainService

/**
 * Payload returned by GET /branding — same shape as BrandIdentityDto on the server.
 */
export interface ShopBrandingPayload {
  shopId: string
  shopCode: string
  shopName: string
  logoUrl?: string | null
  bannerUrl?: string | null
  faviconUrl?: string | null
  themeColor?: string | null
  secondaryColor?: string | null
  fontFamily?: string | null
  taglineText?: string | null
  heroTagline?: string | null
  isWhiteLabel: boolean
}

/**
 * Body for PUT /branding — all optional, server clears any field set to null.
 */
export interface UpdateShopBrandingRequest {
  logoUrl?: string | null
  bannerUrl?: string | null
  faviconUrl?: string | null
  themeColor?: string | null
  secondaryColor?: string | null
  fontFamily?: string | null
  taglineText?: string | null
  heroTagline?: string | null
}
